import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from '../../shared/rbac.service';
import { ToolboxTalksSelectorComponent } from 'src/app/toolbox-talks/toolbox-talks-selector/toolbox-talks-selector.component';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { ToolboxTalkModel } from '../../models/toolbox-talk.model';
import { ToolboxTalksEditComponent } from '../../toolbox-talks/toolbox-talks-edit/toolbox-talks-edit.component';
import { SitesToolboxTalksFilterComponent } from '../sites-toolbox-talks-filter/sites-toolbox-talks-filter.component';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { ToolboxTalksTemplatesSelectorComponent } from '../../toolbox-talks/toolbox-talks-templates-selector/toolbox-talks-templates-selector.component';
import { ToolboxTalksTemplatesSnippetsSelectorComponent } from '../../toolbox-talks/toolbox-talks-templates-snippets-selector/toolbox-talks-templates-snippets-selector.component';
import { ToolboxTalkTemplateModel } from '../../models/toolbox-talk-template.model';

@Component({
  selector: 'app-sites-toolbox-talks',
  templateUrl: './sites-toolbox-talks.component.html',
  styleUrls: ['./sites-toolbox-talks.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesToolboxTalksComponent implements OnInit, AfterViewInit {
  parent_id: number;
  child_id: number;
  site_id: number;

  path: string;

  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Cross-feature permission: copy to templates requires templates-toolbox-talks:create.
  canCreateTemplate: boolean = false;

  // columns to show and the data source
  displayedColumns: string[] = [];
  dataSource: SiteToolboxTalksDataSource;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    public route: ActivatedRoute,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );
    this.canCreateTemplate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );

    // NO page-level redirect for site components (user is already within a site context).

    // 2. Build displayedColumns dynamically.
    this.displayedColumns = [
      ...(this.canView || this.canDelete ? ['select'] : []),
      'site',
      'name',
      'created_by_user',
      'updated_by_user',
      'reported_by_user',
      'date_created',
      'date_modified',
      'actions'
    ];

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;
      // get sites path edit/view
      this.path = this.route.parent.snapshot.routeConfig.path;

      // init the data source
      this.dataSource = new SiteToolboxTalksDataSource(
        this.app,
        this.api,
        true
      );
      this.dataSource.site_ids.push(this.site_id);

      // 3. Only load data if user has view permission.
      if (this.canView) {
        this.dataSource.getData(true);
      }
    }
  }

  ngAfterViewInit() {
    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      } else if (this.sort.active === 'date_modified_UTC') {
        this.dataSource.sort_by = 'date_modified';
      } else {
        this.dataSource.sort_by = this.sort.active;
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onEdit(toolbox_talk_id: number | string) {
    if (toolbox_talk_id === 'new') {
      if (!this.canCreate) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to create toolbox talks.'
        );
        return;
      }
    } else {
      if (!this.canEdit) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to edit toolbox talks.'
        );
        return;
      }
    }
    // open the hazard edit dialog for editing
    this.utils.showComponentDialog(
      ToolboxTalksEditComponent,
      {
        id: toolbox_talk_id,
        site_id: this.site_id,
        redirect_url: this.utils.prepareSiteRelatedLink(
          this.site_id,
          this.child_id ? this.parent_id : null,
          'edit/toolbox-talks/' + toolbox_talk_id + '/view'
        )
      },
      {
        width: '1200px'
      },
      (response) => {
        // Only refresh list if user has view permission to avoid 403.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  onSelectTemplates() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create toolbox talks.'
      );
      return;
    }
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesSelectorComponent,
      {
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.copyToolboxTalksToSite(results.length ? results.join(',') : '');
        }
      }
    );
  }

  onBuildFromSnippets() {
    if (!this.canCreate || !this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create toolbox talks from snippets.'
      );
      return;
    }
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesSnippetsSelectorComponent,
      {
        multiple: true
      },
      {
        width: '1200px'
      },
      (selectedIds: number[]) => {
        if (Array.isArray(selectedIds) && selectedIds.length > 0) {
          this.api
            .makeRequest(
              'get',
              `v1/toolbox-talks-templates/${selectedIds.join(',')}`
            )
            .then((response: any) => {
              const templates = response.data as ToolboxTalkTemplateModel[];
              const merged = this.mergeSnippets(templates);

              this.utils.showComponentDialog(
                ToolboxTalksEditComponent,
                {
                  toolbox_talk: merged,
                  site_id: this.site_id,
                  redirect_url: this.utils.prepareSiteRelatedLink(
                    this.site_id,
                    this.child_id ? this.parent_id : null,
                    'edit/toolbox-talks/'
                  )
                },
                {},
                (response) => {
                  // Only refresh list if user has view permission to avoid 403.
                  if (this.canView) {
                    this.dataSource.getData(true);
                  }
                }
              );
            })
            .catch((err) => this.utils.handleAPIErrors(err));
        }
      }
    );
  }

  mergeSnippets(templates: ToolboxTalkTemplateModel[]): ToolboxTalkModel {
    const mergedContent = templates
      .map((t) => `<h3>${t.name}</h3>\n\n${t.agenda}`)
      .join('\n\n');

    const model = new ToolboxTalkModel();
    model.agenda = mergedContent;
    model.site_id = this.site_id;

    return model;
  }

  onCopyTemplates() {
    if (!this.canCreateTemplate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create toolbox talk templates.'
      );
      return;
    }
    this.utils.showModal(
      'Copy ' +
        this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
        ' Templates',
      'Are you sure you want to copy these to templates?',
      () => {
        const selectedIds = this.dataSource.selection.selected;

        const payload = {
          selected_toolbox_talk_ids:
            selectedIds.length > 0 ? selectedIds : 'all'
        };

        this.api
          .makeRequest(
            'put',
            `v1/sites/${this.site_id}/toolbox-talks-templates`,
            payload
          )
          .then(() => {
            this.dataSource.selection.clear();
            this.utils.showToast(
              this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
                ' template(s) were copied.'
            );
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * @param id the id of the toolbox talk.
   * Remove a single toolbox talk.
   */
  onRemove(id: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete toolbox talks.'
      );
      return;
    }
    this.utils.showModal(
      'Delete ' +
        this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk'),
      'Are you sure you want to delete this ' +
        this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
        '?',
      () => {
        this.onRemoveToolboxTalks([id], (response) => {
          this.dataSource.selection.deselect(id);
        });
      }
    );
  }

  /**
   * Remove selected toolbox talks from the specified site
   */
  onDeleteSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete toolbox talks.'
      );
      return;
    }
    this.utils.showModal(
      'Delete Selected ' +
        this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks'),
      'Are you sure you want to delete the selected ' +
        this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks') +
        '?',
      () => {
        this.onRemoveToolboxTalks(
          this.dataSource.selection.selected,
          (response) => {
            this.dataSource.selection.clear();
          }
        );
      }
    );
  }

  /**
   * open a dialog allowing the user to select toolbox talks.
   */
  onSelectToolboxTalks() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create toolbox talks.'
      );
      return;
    }
    // show the hazard selector
    this.utils.showComponentDialog(
      ToolboxTalksSelectorComponent,
      {
        multiple: true
      },
      {},
      (results) => {
        if (typeof results !== 'undefined') {
          this.copyToolboxTalksToSite(results);
        }
      }
    );
  }

  copyToolboxTalksToSite(selected_toolbox_talk_ids) {
    this.api
      .makeRequest('put', 'v1/sites/' + this.site_id + '/toolbox-talks', {
        selected_toolbox_talk_ids: selected_toolbox_talk_ids
      })
      .then((response: any) => {
        // Only refresh list if user has view permission to avoid 403.
        if (this.canView) {
          this.dataSource.getData(true);
        }
        this.utils.showToast(
          this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
            ' templates were copied.'
        );
      })
      .catch((err: any) => {
        this.utils.showModal('Error', err.error.message);
      });
  }

  onRemoveToolboxTalks(toolbox_talk_ids: number[], callback?: any) {
    if (this.site_id) {
      // Delete the toolbox talk.
      this.api
        .makeRequest(
          'delete',
          'v1/toolbox-talks/' +
            toolbox_talk_ids.join(',') +
            '?site_id=' +
            this.site_id
        )
        .then((response: any) => {
          // reload the data and reset the offset
          this.dataSource.getData(true);

          if (typeof callback === 'function') {
            callback(response);
          }
        })
        .catch((err: any) => {
          this.utils.showModal('Error', err.error.message);
        });
    }
  }

  // Duplicate function in toolbox-talks.component.ts
  onExport(type: string = 'pdf', id?: number) {
    let ids: number[] = [];
    if (id) {
      ids.push(id);
    } else {
      ids.push(...this.dataSource.selection.selected);
    }

    this.api
      .makeDownloadRequest(
        `v1/toolbox-talks/export/${type}` +
          (ids.length > 0 ? '/' + ids.join(',') : ''),
        {},
        {
          site_ids: [this.site_id],
          reported_by_ids: this.dataSource.reported_by_ids
            ? this.dataSource.reported_by_ids.join(',')
            : '',
          employee_ids: this.dataSource.employee_ids
            ? this.dataSource.employee_ids.join(',')
            : '',
          attendee_ids: this.dataSource.attendee_ids
            ? this.dataSource.attendee_ids.join(',')
            : '',
          date_range:
            this.dataSource.date_range && this.dataSource.date_range.length > 1
              ? [
                  this.dataSource.date_range[0].getTime() / 1000,
                  this.dataSource.date_range[1].getTime() / 1000
                ].join(',')
              : '',
          sort_by: this.dataSource.sort_by,
          sort_order: this.dataSource.sort_order
        }
      )
      .then((response) => {
        // handle internet explorer - No longer supported
        // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //   window.navigator.msSaveOrOpenBlob(response);
        //   return;
        // }

        // Get the current date object
        const date = new Date();

        // Create object url to handle file downloads
        const data = window.URL.createObjectURL(response);

        // Create a download link
        const downloadLink = document.createElement('a');
        downloadLink.href = data;
        downloadLink.download =
          this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks') +
          ` - ${date.getFullYear()}${date.getMonth() +
            1}${date.getDate()}.${type}`;
        // Initiate the download
        downloadLink.click();

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(function() {
          window.URL.revokeObjectURL(data);
        }, 300); // Minimum 300 miliseconds
      })
      .catch((errorResponse) => {
        this.utils.showModal('Error', errorResponse.message);
      });
  }

  onCreateFrom(toolboxTalk: ToolboxTalkModel) {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create toolbox talks.'
      );
      return;
    }
    const newToolboxTalk: ToolboxTalkModel = new ToolboxTalkModel();
    newToolboxTalk.createFrom(toolboxTalk);
    this.utils
      .showComponentDialog(
        ToolboxTalksEditComponent,
        {
          site_id: this.site_id,
          toolbox_talk: newToolboxTalk
        },
        {
          width: '1200px'
        }
      )
      .then(() => {
        // Only refresh list if user has view permission to avoid 403.
        if (this.canView) {
          this.dataSource.getData();
        }
      });
  }

  onFilter() {
    this.utils.showComponentDialog(
      SitesToolboxTalksFilterComponent,
      {
        reported_by_ids: this.dataSource.reported_by_ids,
        employee_ids: this.dataSource.employee_ids,
        attendee_ids: this.dataSource.attendee_ids,
        date_range: this.dataSource.date_range,
        site_ids: this.dataSource.site_ids,
        parent_site_id: this.parent_id
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.reported_by_ids =
            results.reported_by_ids || this.dataSource.reported_by_ids;
          this.dataSource.employee_ids =
            results.employee_ids || this.dataSource.employee_ids;
          this.dataSource.attendee_ids =
            results.attendee_ids || this.dataSource.attendee_ids;
          this.dataSource.date_range =
            results.date_range || this.dataSource.date_range;

          this.dataSource.site_ids = results.site_ids;
          if (this.dataSource.site_ids.length === 0) {
            this.dataSource.site_ids.push(this.site_id);
          }

          // Reload the list.
          this.dataSource.getData(true);
        }
      }
    );
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;
}

export class SiteToolboxTalksDataSource extends CustomDataSource {
  sort_by = 'date_created';
  sort_order = 'desc';

  site_ids = []; // The site_ids array must manually be added to the outdated API call.

  reported_by_ids = [];
  employee_ids = [];
  attendee_ids = [];

  date_range: Date[] = [];

  /**
   * Update 08/04/2021: Due to the endpoint being changed from `sites/x/toolbox-talks` to `toolbox-talks` additional info need to be sent to the API to filter records.
   * @param resetOffset Should the results be reset.
   */
  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('toolbox-talks', resetOffset, () => {}, {
      site_ids: this.site_ids ? this.site_ids.join(',') : '',
      reported_by_ids: this.reported_by_ids
        ? this.reported_by_ids.join(',')
        : '',
      employee_ids: this.employee_ids ? this.employee_ids.join(',') : '',
      attendee_ids: this.attendee_ids ? this.attendee_ids.join(',') : '',
      date_range:
        this.date_range && this.date_range.length > 1
          ? [
              this.date_range[0].getTime() / 1000,
              this.date_range[1].getTime() / 1000
            ].join(',')
          : ''
    });
  }
}
